import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CustomList, CustomListItem, CustomListAttribute } from 'src/app/models/custom-list';
import { SystemService, Utilities } from 'src/app/services';
import { ModalConfirmComponent } from 'src/app/components/system/modal-confirm/modal-confirm.component';

@Component({
    selector: 'wm-global-custom-list-editor',
    templateUrl: './global-custom-list-editor.component.html',
    styleUrl: './global-custom-list-editor.component.css',
    standalone: false
})
export class GlobalCustomListEditorComponent implements OnInit {
  @ViewChild('deleteAttributeConfirm') deleteAttributeConfirmModal: ModalConfirmComponent;

  customListId: string;
  customList: CustomList;
  form: UntypedFormGroup;
  showCustomListAttributes = false;
  attributeToDelete: CustomListAttribute = null;
  constructor(
    private _systemSvc: SystemService,
    private _router: Router,
    private _toastr: ToastrService,
    private _route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.form = new UntypedFormGroup({
      name: new UntypedFormControl(null, [Validators.required])
    });

    this._route.params.subscribe(params => {
      this.customListId = params['id'];

      if (this.customListId) {
        this._systemSvc.getGlobalCustomList(this.customListId).subscribe(list => {
          if (list) {
            this.customList = list;
          }
        });
      } else {
        this.customList = {
          id: Utilities.EMPTY_GUID,
          name: null,
          clientId: null,
          items: [],
          attributes: [],
          roleId: null
        };
      }
    });
  }

  remove() {
    this._systemSvc.deleteGlobalCustomList(this.customListId).subscribe(res => {
      this._toastr.success('Removed!');
      this.goBack();
    });
  }

  removeItem(listItem) {
    this._systemSvc
      .deleteGlobalCustomListItem(listItem.id)
      .subscribe(res => {
        this._toastr.success('Item removed');
      });
  }

  promptRemoveAttribute(attribute: CustomListAttribute) {
    this.attributeToDelete = attribute;
    this.deleteAttributeConfirmModal.open();
  }

  confirmRemoveAttribute() {
    if (!this.attributeToDelete) return;

    this._systemSvc
      .deleteGlobalCustomListAttribute(this.attributeToDelete.id)
      .subscribe(res => {
        this._toastr.success('Attribute and all associated values removed');
        const index = this.customList.attributes.indexOf(this.attributeToDelete);
        if (index > -1) {
          this.customList.attributes.splice(index, 1);
        }
        this.attributeToDelete = null;
      });
  }

  addAttribute() {
    if (!this.customList.attributes) {
      this.customList.attributes = [];
    }

    const newAttribute = new CustomListAttribute({
      id: Utilities.generateId(),
      customListId: this.customList.id,
      name: ''
    });

    this.customList.attributes.push(newAttribute);
  }

  save() {
    for (const item of this.customList.items) {
      item.customListId = this.customList.id;
    }

    // Get valid attribute IDs
    const validAttributeIds = (this.customList.attributes || []).map(attr => attr.id);

    const list: CustomList = {
      id: this.customList.id,
      clientId: null,
      name: this.customList.name,
      items: this.customList.items.map(
        (item): CustomListItem => {
          // Filter attribute values to only include those with valid attribute IDs
          const itemAttributeValues = (item as any).attributeValues || [];
          const filteredAttributeValues = itemAttributeValues.filter(
            av => validAttributeIds.includes(av.customListAttributeId)
          );

          return {
            id: item.id,
            customListId: this.customList.id,
            position: item.position,
            text: item.text,
            value: item.value,
            attributeValues: filteredAttributeValues
          };
        }
      ),
      attributes: this.customList.attributes || [],
      roleId: null
    };

    this._systemSvc.saveGlobalCustomList(list).subscribe(res => {
      this._toastr.success('Saved!');
      this.goBack();
    });
  }

  goBack() {
    this._router.navigate([
      '/admin/global/custom-lists'
    ]);
  }

  canRemove() {
    return this.customList.id !== Utilities.EMPTY_GUID;
  }
}
